import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import { IconButton, Menu, MenuItem, Stack } from "@mui/material";
import DownloadIcon from "src/assets/svg-icons/download.svg";
import EditIcon from "src/assets/svg-icons/aggregators/edit.svg";
import DeleteIcon from "src/assets/svg-icons/aggregators/delete.svg";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import { ConfirmPopup } from "src/shared/components/confirm-popup";
import { notificationsState } from "src/store/notifications";
import { APIErrorResponse } from "src/types/generic";
import { SalaryListRow } from "src/types/salary";
import { useDeleteSalarySalary } from "src/api/salary/salaries";
import MenuItemLabel from "src/pages/salary/menu-item-label";
import SalaryModal from "../../add-edit-salary";
import AddPaymentPopup from "../../add-payment-popup";
import AttachmentsPopup from "../../attachments-popup";

const TableActions = ({
  row,
  refetch,
}: {
  row: SalaryListRow;
  refetch: () => void;
}) => {
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const { mutate: delMut, isPending: deleting } = useDeleteSalarySalary();

  const closeMenu = () => setAnchorEl(null);

  const onDelete = () => {
    delMut(row.id, {
      onSuccess: () => {
        setConfirmDelete(false);
        refetch();
        setNotifications([
          ...notifications,
          { type: "success", message: generalT("deletedSuccessfully") },
        ]);
      },
      onError: (err: APIErrorResponse) => {
        setNotifications([
          ...notifications,
          {
            type: "error",
            message: err?.data?.errors?.[0]?.value ?? generalT("somethingWrong"),
          },
        ]);
      },
    });
  };

  return (
    <>
      <Stack direction="row" justifyContent="center" alignItems="center">
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          <MoreVertIcon />
        </IconButton>
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            closeMenu();
            setAttachOpen(true);
          }}
          sx={{ gap: 1 }}
        >
          <img src={DownloadIcon} alt="" />
          <MenuItemLabel>
            {generalT("attachments")}
          </MenuItemLabel>
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            setEditOpen(true);
          }}
          sx={{ gap: 1 }}
        >
          <img src={EditIcon} alt="" />
          <MenuItemLabel>
            {generalT("edit")}
          </MenuItemLabel>
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            setConfirmDelete(true);
          }}
          sx={{ gap: 1 }}
        >
          <img src={DeleteIcon} alt="" />
          <MenuItemLabel>
            {generalT("delete")}
          </MenuItemLabel>
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            navigate(`/salary/salaries/${row.id}`);
          }}
          sx={{ gap: 1 }}
        >
          <VisibilityOutlinedIcon sx={{ fontSize: "16px" }} />
          <MenuItemLabel>
            {generalT("details")}
          </MenuItemLabel>
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            setPayOpen(true);
          }}
          sx={{ gap: 1 }}
        >
          <PaidOutlinedIcon sx={{ fontSize: "16px" }} />
          <MenuItemLabel>
            {`${generalT("pay")}!`}
          </MenuItemLabel>
        </MenuItem>
      </Menu>

      <ConfirmPopup
        open={confirmDelete}
        handleClose={() => setConfirmDelete(false)}
        handleConfirm={onDelete}
        isPending={deleting}
        item={row.reference}
      />

      <SalaryModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        salaryId={row.id}
        onSaved={() => {
          setEditOpen(false);
          refetch();
        }}
      />

      <AddPaymentPopup
        open={payOpen}
        onClose={() => setPayOpen(false)}
        salaryId={row.id}
        salaryReference={row.reference}
        onPaid={() => {
          setPayOpen(false);
          refetch();
        }}
      />

      <AttachmentsPopup
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        salaryId={row.id}
        salaryReference={row.reference}
      />
    </>
  );
};

export default TableActions;
